import { ValidatorFn, Validators } from '@angular/forms';
import { WorkspaceDatabaseKeys } from '@database/workspace';
import { TranslateService } from '@ngx-translate/core';
import { ColumnModel } from '@shared';
import { IValidationConstraint, IValidationsBuildResult, ValidationKeys } from '../models';
import { checkDecimalPlaces, checkNumberOfDigits, checkSpecialChars, fromToValidator } from '../validator-functions';
import { ColumnInformationService } from './column-information.service';
import { ValidationMessagesProviderService } from './validation-messages-provider.service';

export class ValidationRulesBuilderService {
	private validationMessagesDictionary: Record<string, string>[];
	private validatorFnMap: Record<string, ValidatorFn[]>;
	private columnObject: ColumnModel;
	private primaryKeys: string[];
	private isPrimaryKey: boolean;
	private validationMessagesProvider: ValidationMessagesProviderService;

	public constructor(
		private translateService: TranslateService,
		private columnInformationService: ColumnInformationService
	) {
		this.validationMessagesProvider = new ValidationMessagesProviderService(this.translateService);
	}

	public setColumnObject(columnMetadata: ColumnModel): ValidationRulesBuilderService {
		this.columnObject = columnMetadata;
		return this;
	}

	public setPrimaryKeys(primaryKeys: string[]) {
		this.primaryKeys = primaryKeys;
	}

	public build(): IValidationsBuildResult {
		this.setIsPrimaryKey();
		if (!this.columnObject) {
			throw new Error('Column object not initialized');
		}
		if (!this.primaryKeys) {
			throw new Error('Primary keys not initialized');
		}

		const props = Object.keys(this.columnObject);
		const columnName = this.columnObject[WorkspaceDatabaseKeys.columns.columnName];

		this.validatorFnMap = {};
		this.validationMessagesDictionary = [];
		this.validatorFnMap[columnName] = [];

		this.handleCrossFieldValidations(columnName);

		if (this.isPrimaryKey && this.columnInformationService.isColumnTypeChar(this.columnObject)) {
			this.addWhitespacesOnlyInTheMiddleColumnValidation(columnName);
		}

		if (this.isPrimaryKey) {
			this.addColumnValidation(
				columnName,
				checkSpecialChars(/^[^'",;~@#*$]*$/, ValidationKeys.validationTypes.noSpecialCharacter)
			);
			this.addColumnValidationMsg(ValidationKeys.validationTypes.noSpecialCharacter, this.columnObject.data_type);
		}

		this.handleNumericColumnTypeValidation(columnName);

		props.forEach((propertyName) => {
			switch (propertyName) {
				case ValidationKeys.validationRules.requiredRule:
					if (this.columnInformationService.isColumnRequired(this.columnObject)) {
						this.addColumnValidation(columnName, Validators.required);
					}
					this.addColumnValidationMsg(ValidationKeys.validationTypes.required);
					this.addWhitespacesOnlyInTheMiddleColumnValidation(columnName);
					break;
				case ValidationKeys.validationRules.minimumValueRule:
					this.addColumnValidation(columnName, Validators.min(this.columnObject[propertyName]));
					this.addColumnValidationMsg(
						ValidationKeys.validationTypes.minimumValue,
						this.columnObject[propertyName]
					);
					break;
				case ValidationKeys.validationRules.maximumValueRule:
					this.addColumnValidation(columnName, Validators.max(this.columnObject[propertyName]));
					this.addColumnValidationMsg(
						ValidationKeys.validationTypes.maximumValue,
						this.columnObject[propertyName]
					);
					break;
				case ValidationKeys.validationRules.decimalPlacesRule:
					if (!this.columnInformationService.isColumnTypeNumeric(this.columnObject)) {
						break;
					}
					this.addColumnValidation(columnName, checkDecimalPlaces(this.columnObject[propertyName]));
					this.addColumnValidationMsg(
						ValidationKeys.validationTypes.decimalPlaces,
						this.columnObject[propertyName]
					);

					break;
				case ValidationKeys.validationRules.maximumLength: {
					let defaultMaxLenthValidation = Validators.maxLength(this.columnObject[propertyName]);

					if (this.columnInformationService.isColumnTypeNumeric(this.columnObject)) {
						defaultMaxLenthValidation = checkNumberOfDigits(this.columnObject[propertyName]);
					}

					this.addColumnValidation(columnName, defaultMaxLenthValidation);
					this.addColumnValidationMsg(
						ValidationKeys.validationTypes.maximumLength,
						this.columnObject[propertyName],
						this.columnInformationService.isColumnTypeNumeric(this.columnObject)
					);

					break;
				}
			}
		});

		return {
			validations: this.validatorFnMap,
			messages: this.validationMessagesDictionary
		};
	}

	private handleCrossFieldValidations(columnName: string) {
		if (ValidationKeys.crossFieldValidations[columnName]) {
			const crossFieldName = ValidationKeys.crossFieldValidations[columnName].crossFieldName;
			const validationAgainstCrossField = ValidationKeys.crossFieldValidations[columnName].validation;
			const validationAgainstField = ValidationKeys.crossFieldValidations[crossFieldName].validation;

			this.addColumnValidation(
				columnName,
				fromToValidator(crossFieldName, validationAgainstField, validationAgainstCrossField)
			);
			this.addColumnValidationMsg(validationAgainstCrossField);
		}
	}

	private addWhitespacesOnlyInTheMiddleColumnValidation(columnName: string) {
		// Regex allows whitespaces in the middle of characters
		this.addColumnValidation(
			columnName,
			checkSpecialChars(/^[^\s]+(\s+[^\s]+)*$/, ValidationKeys.validationTypes.whitespacesOnlyInTheMiddle)
		);
		this.addColumnValidationMsg(
			ValidationKeys.validationTypes.whitespacesOnlyInTheMiddle,
			this.columnObject.data_type
		);
	}

	private handleNumericColumnTypeValidation(columnName: string) {
		if (this.columnInformationService.isColumnTypeNumeric(this.columnObject)) {
			// Regex allows only numeric characters, with either + or - in the beginning
			this.addColumnValidation(
				columnName,
				checkSpecialChars(/^[-+]?[0-9]*\.?[0-9]+$/, ValidationKeys.validationTypes.noSpecialCharacter)
			);
			this.addColumnValidationMsg(ValidationKeys.validationTypes.noSpecialCharacter, this.columnObject.data_type);
		}
	}

	private addColumnValidation(columnName: string, validation: ValidatorFn) {
		this.validatorFnMap[columnName].push(validation);
	}

	private setIsPrimaryKey() {
		this.isPrimaryKey = this.primaryKeys.includes(this.columnObject.column_name);
	}

	private addColumnValidationMsg(
		validationType: string,
		constraint?: IValidationConstraint,
		isColumnNumeric?: boolean
	) {
		this.validationMessagesDictionary.push(
			this.validationMessagesProvider.getCustomValidationMessage(validationType, constraint, isColumnNumeric)
		);
	}
}
